import type { DailyEntry, GameId, LeaderboardEntry } from '@xox/shared'
import { ChevronLeft, ChevronRight, Flame } from 'lucide-react'
import { useState } from 'react'
import { Avatar } from '../components/Avatar'
import { Card, IconButton, Segmented } from '../components/ui'
import { DailyBoard } from '../dungeon/DailyBoard'
import { useFetch } from '../hooks/useFetch'
import { api } from '../lib/api'
import { useSession } from '../store/session'

type Tab = GameId | 'dungeon'

/** Stable per-game loaders so `useFetch` can treat them as module constants. */
const LOADERS: Record<GameId, () => Promise<LeaderboardEntry[]>> = {
  xox: () => api.leaderboard('xox'),
  c4: () => api.leaderboard('c4'),
}

export function Leaderboard() {
  const [game, setGame] = useState<Tab>('xox')
  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-black tracking-tight">Leaderboard</h1>
        <Segmented<Tab>
          label="Game"
          value={game}
          onChange={setGame}
          className="w-full sm:w-auto"
          options={[
            { value: 'xox', label: 'XOX' },
            { value: 'c4', label: 'Connect Four' },
            { value: 'dungeon', label: 'Dungeon' },
          ]}
        />
      </div>
      {/* Remount per game: the fetch hook loads once per mount. */}
      {game === 'dungeon' ? <DungeonBoards /> : <LeaderboardTable key={game} game={game} />}
    </div>
  )
}

/** Today's dungeon board with a stepper back through previous days. */
function DungeonBoards() {
  const [offset, setOffset] = useState(0)
  const day = dayAgo(offset)
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <IconButton label="Previous day" onClick={() => setOffset((o) => o + 1)}>
          <ChevronLeft className="size-5" aria-hidden />
        </IconButton>
        <span className="text-sm font-semibold tabular-nums">{offset === 0 ? 'Today' : day}</span>
        <IconButton
          label="Next day"
          onClick={() => setOffset((o) => Math.max(0, o - 1))}
          disabled={offset === 0}
        >
          <ChevronRight className="size-5" aria-hidden />
        </IconButton>
      </div>
      <DungeonDay key={day} day={offset === 0 ? undefined : day} />
    </div>
  )
}

function DungeonDay({ day }: { day: string | undefined }) {
  const me = useSession((s) => s.me)
  const { data, error } = useFetch(() => api.dungeonBoard(day))
  if (error) return <p className="text-rose-600">{error}</p>
  return (
    <DailyBoard
      entries={(data ?? []) as DailyEntry[]}
      me={me?.playerId ?? null}
      emptyText={data ? 'Nobody delved that day.' : 'Loading…'}
    />
  )
}

/** A calendar day `n` days ago in the browser's zone (close enough for browsing old boards). */
function dayAgo(n: number): string {
  const d = new Date(Date.now() - n * 86_400_000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function LeaderboardTable({ game }: { game: GameId }) {
  const me = useSession((s) => s.me)
  const { data: rows, error } = useFetch(LOADERS[game])
  return (
    <Card className="overflow-x-auto p-0">
      {error && <p className="p-5 text-rose-600">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="p-5 text-slate-500">No finished games yet. Be the first to win one.</p>
      )}
      {rows && rows.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Player</th>
              <th className="px-4 py-3 text-right">W</th>
              <th className="px-4 py-3 text-right">L</th>
              <th className="px-4 py-3 text-right">Win %</th>
              <th className="px-4 py-3 text-right">Streak</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isMe = r.playerId === me?.playerId
              const pct = r.games ? Math.round((r.wins / r.games) * 100) : 0
              return (
                <tr
                  key={r.playerId}
                  className={[
                    'border-t border-slate-100 dark:border-slate-800',
                    isMe ? 'bg-sky-50 font-semibold dark:bg-sky-950/40' : '',
                  ].join(' ')}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <Avatar id={r.playerId} avatar={r.avatar} color={r.color} size="sm" />
                      {r.nickname}
                      {isMe && <span className="text-xs text-sky-600">(you)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.wins}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.losses}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{pct}%</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.streak >= 3 && (
                      <Flame className="mr-1 inline size-3.5 text-orange-500" aria-hidden />
                    )}
                    {r.streak > 0 ? `+${r.streak}` : r.streak}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}
